import type { ReactNode } from 'react';
import React, { useContext } from 'react';
import classnames from 'clsx';
import type { DraftBlockType, SelectionState } from 'draft-js';
import { EditorState, RichUtils } from 'draft-js';
import type { ToolbarChildrenProps } from 'draft-js-inline-toolbar-plugin';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import styles from '../button.scss';
import StylesContext from '../../../StylesContext';
import semanticClassNames from '../../../../RichTextBox.semanticClassNames';

export type BlockStyleButtonProps = ToolbarChildrenProps & {
  children: ReactNode;
  blockType: DraftBlockType;
  dataTestId?: string;
  lastSelectionBeforeKeyboardNavigation?: SelectionState;
};
export default (props: BlockStyleButtonProps) => {
  const {
    blockType,
    children,
    setEditorState,
    getEditorState,
    dataTestId,
    lastSelectionBeforeKeyboardNavigation: lastSelection,
  } = props;
  const stylesContext = useContext(StylesContext);

  const toggleStyle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const isKeyboardEvent = event.detail === 0;
    const refEditorState =
      lastSelection && isKeyboardEvent
        ? EditorState.forceSelection(getEditorState(), lastSelection)
        : getEditorState();
    setEditorState(RichUtils.toggleBlockType(refEditorState, blockType));
  };

  const preventBubblingUp = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const selected =
    RichUtils.getCurrentBlockType(getEditorState()) === blockType;

  return (
    <div className={styles.buttonContainer}>
      <button
        onMouseDown={preventBubblingUp}
        className={classnames(styles.button, {
          [styles.hovered]: stylesContext.hovered,
          [styles.focused]: stylesContext.focused,
          [styles.error]: stylesContext.error,
        })}
        onClick={toggleStyle}
        data-testid={dataTestId}
        aria-pressed={selected ?? false}
        type="button"
      >
        <div
          className={classnames(
            styles.buttonContent,
            formatClassNames(semanticClassNames.icons),
            selected && styles.buttonContentSelected,
          )}
        >
          {children}
        </div>
      </button>
    </div>
  );
};
